import { useCallback } from 'react';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import {
    getRegisterLoyaltyAndReassignOrderUrl,
    RegisterLoyaltyAndReassignOrderMethod,
    type RegisterLoyaltyAndReassignOrderPathParameters,
    type RegisterLoyaltyAndReassignOrderSearchParams,
} from '@/shared/api/openApi';
import { type OrderDto } from '@/shared/api/order';
import { request } from '@/shared/api/request';
import { getRequestContext } from '@/shared/lib/requestContext';
import { upsertGetOrderQueryData } from './api';

type RegisterLoyaltyAndReassignOrderRequestParams = RegisterLoyaltyAndReassignOrderSearchParams &
    RegisterLoyaltyAndReassignOrderPathParameters;

const loyaltyApi = createApi({
    name: 'entities/order/api/loyalty',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            RegisterLoyaltyAndReassignOrder: builder.mutation<OrderDto, RegisterLoyaltyAndReassignOrderRequestParams>({
                query: (payload) => {
                    return {
                        url: getRegisterLoyaltyAndReassignOrderUrl(payload),
                        method: RegisterLoyaltyAndReassignOrderMethod,
                        params: { signature: payload.signature },
                    };
                },
                transformResponse: (response: OrderDto, meta, arg) =>
                    upsertGetOrderQueryData(response, meta, {
                        order_id: arg.order_id,
                        signature: arg.signature,
                        store_slug: arg.store_slug,
                    }),
            }),
        };
    },
});

interface UseRegisterLoyaltyAndReassignOrderProps {
    onSuccess?: VoidFunction;
}

export function useRegisterLoyaltyAndReassignOrder(props: UseRegisterLoyaltyAndReassignOrderProps = {}) {
    const { onSuccess } = props;

    const { signature, orderId, storeSlug } = getRequestContext();

    const [registerLoyalty, result] = loyaltyApi.useRegisterLoyaltyAndReassignOrderMutation();

    const handleRegisterLoyalty = useCallback(async () => {
        try {
            await registerLoyalty({ signature, order_id: orderId, store_slug: storeSlug }).unwrap();
            onSuccess?.();
        } catch {
            // Ошибка обрабатывается через result.error
        }
    }, [onSuccess, orderId, registerLoyalty, signature, storeSlug]);

    return [handleRegisterLoyalty, result] as const;
}
